import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { scrollBarStyles } from '@/utils/theme';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import CircleUploadedImageRow from './CircleUploadedImageRow';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationStyles from './VariationStyles';

const DEFAULT_FONT_SIZE = 55;

export interface ImageVariation {
  imageSrc: string;
  watermarkTag: string;
  watermarkPosition: string;
  circleImageSrc?: string;
  text: string;
  textFontSize?: number;
  textBrakes?: boolean;
}

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const TextItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextAreaBox = styled.div`
  padding: 24px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  flex-grow: 1;
  align-content: start;
`;

const BottomTextAreaBox = styled(TextAreaBox)`
  max-height: 437px;
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  ${scrollBarStyles}
`;

const TopTextArea = styled(Textarea)`
  height: 90px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 209px;
`;

const StyledUploadedImages = styled(UploadedImages)`
  height: 360px;
`;

const LabelHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ImagesBox = styled.div<{
  $hasImages?: boolean;
  $hasLeftBorder?: boolean;
}>`
  padding: 24px;
  ${({ $hasImages }) =>
    $hasImages &&
    css`
      padding-bottom: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
  ${({ $hasLeftBorder }) =>
    $hasLeftBorder &&
    css`
      border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
    `}
`;

const TextLeft = styled.div`
  display: flex;
  gap: 24px;
`;

const StyledRemoveButton = styled(RemoveButton)`
  background: transparent;
  height: auto;
  padding: 0;
  margin-bottom: 8px;
  text-decoration: underline;
  transition: all 0.2s;
  &:hover {
    border-color: transparent;
    opacity: 0.8;
  }
`;

const CounterBadge = styled(Text)`
  background: ${({ theme }) => theme.colors.purple};
  width: 20px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddMoreHolder = styled.div<{ $inABox?: boolean }>`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  ${({ $inABox }) =>
    $inABox &&
    css`
      padding-bottom: 0;
      margin-left: -24px;
      margin-right: -24px;
      margin-bottom: -8px;
    `}
`;

const StyeldAddMoreButton = styled(AddMoreButton)`
  margin-left: auto;
`;

interface Props {
  inArticle?: boolean;
}

type SingleTextItem = {
  text: string;
  brakes: boolean;
  fontSize?: number;
};

const VariationNewsShareImage: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);

  const [texts, setTexts] = useState<SingleTextItem[]>([
    {
      text: '',
      brakes: false,
      fontSize: DEFAULT_FONT_SIZE,
    },
  ]);

  const [multipleVariations, setMultipleVariations] = useState(false);

  const [fullImages, setFullImages] = useState<ImageData[]>([]);
  const [circleImages, setCircleImages] = useState<ImageData[]>([]);

  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [showMoreCircleImagesModal, setShowMoreCircleImagesModal] =
    useState(false);

  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();

  // ---- Top text handlers ----
  const addText = () =>
    setTexts((prev) => [
      ...prev,
      {
        text: '',
        brakes: false,
        fontSize: DEFAULT_FONT_SIZE,
      },
    ]);

  const updateText = (idx: number, value: string) =>
    setTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, text: value } : t)),
    );

  const toggleTextFlag = (
    idx: number,
    key: keyof Pick<SingleTextItem, 'brakes'>,
  ) => {
    setTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [key]: !t[key] } : t)),
    );
  };

  const removeText = (idx: number) =>
    setTexts((prev) => prev.filter((_, i) => i !== idx));

  const updateFontSize = (idx: number, size: string) =>
    setTexts((prev) =>
      prev.map((b, i) =>
        i === idx
          ? {
              ...b,
              fontSize: size === '' ? undefined : parseInt(size) || undefined,
            }
          : b,
      ),
    );

  const onReset = () => {
    setFullImages([]);
    setCircleImages([]);
    setVariations([]);
    setImages([]);
    setTexts([
      {
        text: '',
        brakes: false,
        fontSize: DEFAULT_FONT_SIZE,
      },
    ]);
  };

  useEffect(() => {
    if (!multipleVariations) {
      setTexts((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  }, [multipleVariations]);

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const useFullImages = fullImages;
    const useCircleImages = circleImages.length
      ? circleImages
      : [undefined as unknown as ImageData];

    const newVariations: ImageVariation[] = useFullImages.flatMap((image) => {
      const imageSrc = image.croppedImage ?? URL.createObjectURL(image.image);

      return useCircleImages.flatMap((circle) => {
        const circleImageSrc =
          circle && (circle as unknown as ImageData).image
            ? circle.croppedImage ?? URL.createObjectURL(circle.image)
            : undefined;

        return texts.map((t) => ({
          imageSrc,
          watermarkTag: getWatermarkTag(image),
          watermarkPosition: getWatermarkPosition(image),
          circleImageSrc,
          text: t.text,
          textFontSize: t.fontSize,
          textBrakes: t.brakes,
        }));
      });
    });

    setVariations(newVariations);
  };

  const renderFullImages = fullImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setFullImages}
    />
  ));

  const renderCircleImages = circleImages.map((image, index) => (
    <CircleUploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setCircleImages}
    />
  ));

  const isResetDisabled =
    !circleImages?.length && !fullImages?.length && !texts.length;

  return (
    <>
      <VariationStyles />
      <Box>
        <Filler fontFamily="SourceSansProBlack" fontWeight={900} />
        <Header>
          <div>
            <Heading $type="button">News Share Image</Heading>
            <Text $type="smallHeadline" $color="gray">
              Add the cover and bottom text block alongside with oval image
            </Text>
          </div>
          <HeaderButtons>
            <FancySwitch
              isActive={multipleVariations}
              onClick={() => setMultipleVariations((prev) => !prev)}
              text="Multiple Variations:"
            />
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>

        {showContent && (
          <>
            <ImageGrid>
              <ImagesBox $hasImages={!!fullImages?.length}>
                <LabelText $type="smallHeadline">Cover Image</LabelText>
                {!!fullImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderFullImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={fullImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    noneDivider
                    setImages={setFullImages}
                  />
                )}
              </ImagesBox>
              <ImagesBox $hasLeftBorder $hasImages={!!circleImages?.length}>
                <LabelText $type="smallHeadline">Oval Image</LabelText>
                {!!circleImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderCircleImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={circleImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreCircleImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    noneDivider
                    setImages={setCircleImages}
                  />
                )}
              </ImagesBox>
            </ImageGrid>
            <BottomTextAreaBox>
              {texts.map((item, idx) => (
                <TextItem key={idx}>
                  <div>
                    <LabelHolder>
                      <LabelText $type="smallHeadline">
                        Bottom Text
                        {texts.length > 1 && (
                          <CounterBadge $type="extraSmall" $color="white">
                            {idx + 1}
                          </CounterBadge>
                        )}
                      </LabelText>
                      {texts.length > 1 && (
                        <StyledRemoveButton
                          color="red"
                          onClick={() => removeText(idx)}
                        >
                          Remove
                        </StyledRemoveButton>
                      )}
                    </LabelHolder>
                    <TopTextArea
                      onChange={(e) => updateText(idx, e.target.value)}
                      value={item.text}
                      placeholder="Add text"
                    />
                  </div>
                  <div>
                    <LabelText $type="smallHeadline">Font Size, px</LabelText>
                    <Input
                      type="number"
                      min="0"
                      value={item.fontSize ?? ''}
                      onChange={(e) => updateFontSize(idx, e.target.value)}
                      placeholder="Add Font Size"
                    />
                  </div>
                  <TextLeft>
                    <Checkbox
                      checked={!!item.brakes}
                      onChange={() => toggleTextFlag(idx, 'brakes')}
                    >
                      Custom brakes
                    </Checkbox>
                  </TextLeft>
                </TextItem>
              ))}
            </BottomTextAreaBox>
            {multipleVariations && (
              <AddMoreHolder>
                {texts.length > 1 && (
                  <AttachedCountBadge
                    count={texts.length}
                    text="Bottom Texts"
                  />
                )}
                <StyeldAddMoreButton color="colorized" onClick={addText}>
                  Add bottom text
                </StyeldAddMoreButton>
              </AddMoreHolder>
            )}
          </>
        )}
      </Box>

      {showContent && (
        <ButtonHolder>
          <Button disabled={!fullImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}

      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />

      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setFullImages}
            coverIndex={1}
            noneDivider
          />
        )}
        {!!showMoreCircleImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreCircleImagesModal(false)}
            setImages={setCircleImages}
            coverIndex={1}
            noneDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationNewsShareImage;
